import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Plus, ChevronLeft, ChevronRight, Check, X, Trash2, Calendar, FileText, Edit3 } from 'lucide-react';
import { wallTime, wallDay, wallHour, wallDateTime, localDateStr, localInputValue } from '../../lib/time';
import { safeConfirm } from '../../lib/confirm';
import { toast } from 'sonner';

function startOfWeek(d) { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0,0,0,0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate()+n); return x; }
function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x; }
function endOfMonth(d) { const x = new Date(d); x.setMonth(x.getMonth()+1, 0); x.setHours(23,59,59,999); return x; }
function sameDay(a, b) { return a.toDateString() === b.toDateString(); }
const formatDate = localDateStr;
let CLOCK = '24h';
const formatTime = (iso) => wallTime(iso, CLOCK);

export default function AdminCalendar() {
  const [view, setView] = useState('month');
  const [cursor, setCursor] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ patient_id: '', starts_at: '', duration_min: 45, price: 200, currency: 'PLN', notes: '', therapist_name: '', service_id: '' });
  const [settings, setSettings] = useState({ supported_currencies: ['PLN','EUR','USD','CAD','XCD'] });
  const [toast, setToast] = useState('');
  // Iter 67 — appointment quick-action popover triggered from calendar pill click.
  const [openAppt, setOpenAppt] = useState(null);

  const load = () => api.get('/appointments').then(r => setAppts(r.data));
  useEffect(() => {
    load();
    api.get('/patients').then(r => setPatients(r.data));
    api.get('/services').then(r => setServices(r.data));
    api.get('/clinic/settings').then(r => { setSettings(r.data); CLOCK = r.data?.clock_format || '24h'; });
  }, []);

  const selectService = (id) => {
    const svc = services.find(s => s.service_id === id);
    if (svc) setForm(f => ({ ...f, service_id: id, duration_min: svc.duration_min, price: svc.price, currency: svc.currency }));
    else setForm(f => ({ ...f, service_id: '' }));
  };

  const submit = async () => {
    try {
      const { data } = await api.post('/appointments', {
        ...form,
        duration_min: parseInt(form.duration_min),
        price: parseFloat(form.price),
        therapist_name: form.therapist_name || settings.therapist_name,
      });
      setShow(false);
      setToast(`Appointment booked for ${data.patient_name} on ${new Date(data.starts_at).toLocaleString()}`);
      setTimeout(() => setToast(''), 4000);
      // Switch to the day view of the new appointment so user sees it immediately
      const d = new Date(data.starts_at);
      if (!isNaN(d)) { setCursor(d); setView('day'); }
      load();
    } catch (e) {
      setToast(`Booking failed: ${e.response?.data?.detail || e.message}`);
      setTimeout(() => setToast(''), 5000);
    }
  };

  const setStatus = async (a, status) => { await api.put(`/appointments/${a.appointment_id}`, { status }); load(); };
  const remove = async (a) => { if (!await safeConfirm('Delete this appointment? It moves to "Recently deleted" (Appointments page) — restorable for 30 days.')) return; await api.delete(`/appointments/${a.appointment_id}`); toast.success('Appointment moved to Recently deleted — restore it from the Appointments page within 30 days.'); load(); };

  const reschedule = async (a, newDateObj) => {
    // Preserves duration and other fields — only shifts starts_at.
    // Iter 136 — send a naive clinic-local string (never toISOString: that
    // converts to UTC and shifts the hour on non-UTC devices).
    try {
      const naive = localInputValue(newDateObj);
      await api.put(`/appointments/${a.appointment_id}`, {
        starts_at: naive,
      });
      setToast(`Rescheduled to ${wallDateTime(naive, CLOCK)}`);
      setTimeout(() => setToast(''), 3500);
      load();
    } catch (e) {
      setToast(`Reschedule failed: ${e.response?.data?.detail || e.message}`);
      setTimeout(() => setToast(''), 5000);
    }
  };

  const shift = (n) => {
    const x = new Date(cursor);
    if (view === 'year') x.setFullYear(x.getFullYear() + n);
    else if (view === 'month') x.setMonth(x.getMonth() + n);
    else if (view === 'week') x.setDate(x.getDate() + 7*n);
    else x.setDate(x.getDate() + n);
    setCursor(x);
  };

  const title = useMemo(() => {
    if (view === 'year') return cursor.getFullYear();
    if (view === 'month') return cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    if (view === 'week') {
      const s = startOfWeek(cursor); const e = addDays(s, 6);
      return `${s.toLocaleDateString(undefined,{month:'short',day:'numeric'})} – ${e.toLocaleDateString(undefined,{month:'short',day:'numeric',year:'numeric'})}`;
    }
    return cursor.toLocaleDateString(undefined, { weekday:'long', month:'long', day:'numeric', year:'numeric' });
  }, [cursor, view]);

  const apptsOnDay = (d) => { const key = localDateStr(d); return appts.filter(a => wallDay(a.starts_at) === key); };

  const openBook = (d) => {
    const base = new Date(d); base.setHours(9, 0, 0, 0);
    setForm(f => ({ ...f, starts_at: localInputValue(base) }));
    setShow(true);
  };

  const jumpTo = (patch) => {
    const x = new Date(cursor);
    if (patch.month !== undefined) x.setMonth(patch.month);
    if (patch.year !== undefined) x.setFullYear(patch.year);
    setCursor(x);
  };
  const yearOptions = [];
  for (let y = new Date().getFullYear() - 2; y <= new Date().getFullYear() + 3; y++) yearOptions.push(y);

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6 print:hidden">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">SCHEDULE</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Calendar</h1>
          {/* Iter 68 — discipline color legend */}
          <div className="hidden md:flex items-center gap-3 mt-2 flex-wrap" data-testid="cal-discipline-legend">
            {Object.entries({
              physiotherapy: ['#003D80','Physio'],
              osteopathy: ['#7B4FA5','Osteo'],
              chiropractic: ['#D86E2C','Chiro'],
              massage_therapy: ['#5A8A6A','Massage'],
              fitness: ['#FFB000','Fitness'],
            }).map(([key, [color, lbl]]) => (
              <span key={key} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-fg-muted">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }}/>
                {lbl}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-soft overflow-hidden">
            {['day','week','month','year'].map(v => (
              <button key={v} onClick={()=>setView(v)} className={`px-3 py-1.5 text-sm ${view===v?'bg-brand text-white':'bg-white text-fg-muted'}`} data-testid={`view-${v}`}>
                {v[0].toUpperCase()+v.slice(1)}
              </button>
            ))}
          </div>
          <button onClick={()=>setCursor(new Date())} className="btn-secondary text-xs" data-testid="today">Today</button>
          <button onClick={()=>shift(-1)} className="p-2 border border-soft rounded-md" data-testid="prev"><ChevronLeft className="w-4 h-4"/></button>
          {/* Iter 136 — direct month + year dropdowns so nobody has to arrow
              through 12 months to reach a date. */}
          <select className="input !py-1.5 !px-2 !text-sm !w-auto" value={cursor.getMonth()}
                  onChange={e=>jumpTo({ month: parseInt(e.target.value, 10) })} data-testid="cal-month-select">
            {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
              <option key={m} value={i}>{m}</option>
            ))}
          </select>
          <select className="input !py-1.5 !px-2 !text-sm !w-auto" value={cursor.getFullYear()}
                  onChange={e=>jumpTo({ year: parseInt(e.target.value, 10) })} data-testid="cal-year-select">
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={()=>shift(1)} className="p-2 border border-soft rounded-md" data-testid="next"><ChevronRight className="w-4 h-4"/></button>
          <button onClick={()=>setShow(s=>!s)} className="btn-primary inline-flex items-center gap-2" data-testid="new-appt-btn"><Plus className="w-4 h-4"/> New</button>
        </div>
      </div>
      <div className="text-sm font-medium mb-3 print:hidden" data-testid="cal-title">{title}</div>

      {toast && (
        <div className="mb-4 surface border border-soft rounded-lg p-3 text-sm" data-testid="cal-toast">
          {toast}
        </div>
      )}

      {show && (
        <div className="admin-card mb-6">
          <h3 className="font-heading font-semibold mb-3">New appointment</h3>
          <div className="grid md:grid-cols-3 gap-3">
            <F label="Patient">
              <select className="input" value={form.patient_id} onChange={e=>setForm({...form, patient_id: e.target.value})} data-testid="appt-patient">
                <option value="">Select…</option>
                {[...patients]
                  .sort((a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || ''))
                  .map(p => <option key={p.user_id} value={p.user_id}>{p.full_name || p.email || p.patient_code || p.user_id}</option>)}
              </select>
              {patients.length === 0 && (
                <div className="text-[11px] text-fg-muted mt-1" data-testid="appt-no-patients">
                  No patients yet — add one under <Link to="/admin/patients" className="text-brand underline">Patients</Link> first.
                </div>
              )}
            </F>
            <F label="Service">
              <select className="input" value={form.service_id} onChange={e=>selectService(e.target.value)} data-testid="appt-service">
                <option value="">Custom</option>
                {services.filter(s=>s.active).map(s => <option key={s.service_id} value={s.service_id}>{`${s.name} · ${s.price} ${s.currency} · ${s.duration_min}m`}</option>)}
              </select>
            </F>
            <F label="Starts at"><input type="datetime-local" className="input" value={form.starts_at} onChange={e=>setForm({...form, starts_at: e.target.value})} data-testid="appt-time"/></F>
            <F label={form.service_id ? 'Duration (min) — locked by service' : 'Duration (min)'}>
              <input type="number" className={`input ${form.service_id ? 'opacity-70 cursor-not-allowed' : ''}`}
                     value={form.duration_min} disabled={!!form.service_id} readOnly={!!form.service_id}
                     title={form.service_id ? 'Set by the selected service — choose "Custom" to override' : ''}
                     onChange={e=>setForm({...form, duration_min: e.target.value})} data-testid="appt-duration"/>
            </F>
            <F label={form.service_id ? 'Price — locked by service' : 'Price'}>
              <input type="number" step="0.01" className={`input ${form.service_id ? 'opacity-70 cursor-not-allowed' : ''}`}
                     value={form.price} disabled={!!form.service_id} readOnly={!!form.service_id}
                     title={form.service_id ? 'Set by the selected service — choose "Custom" to override' : ''}
                     onChange={e=>setForm({...form, price: e.target.value})} data-testid="appt-price"/>
            </F>
            <F label="Currency">
              <select className="input" value={form.currency} onChange={e=>setForm({...form, currency: e.target.value})}>
                {settings.supported_currencies?.map(c => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="Notes" full><input className="input" value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})}/></F>
          </div>
          <div className="mt-3 flex justify-end">
            <button onClick={submit} className="btn-primary" disabled={!form.patient_id || !form.starts_at} data-testid="appt-save">Save</button>
          </div>
        </div>
      )}

      {view === 'year' && <YearView cursor={cursor} appts={appts} onMonthClick={(d)=>{setCursor(d); setView('month');}} />}
      {view === 'month' && <MonthView cursor={cursor} apptsOnDay={apptsOnDay} onDayClick={(d)=>{setCursor(d); setView('day');}} onBook={openBook} onApptClick={setOpenAppt}/>}
      {view === 'week' && <WeekView cursor={cursor} apptsOnDay={apptsOnDay} onDayClick={(d)=>{setCursor(d); setView('day');}} onApptClick={setOpenAppt}/>}
      {view === 'day' && <DayView cursor={cursor} appts={apptsOnDay(cursor)} onBook={openBook} onStatus={setStatus} onRemove={remove} onReschedule={reschedule} onApptClick={setOpenAppt}/>}

      {/* Iter 67 — quick-action popover triggered from clicking any appointment pill. */}
      {openAppt && <AppointmentPopover appt={openAppt} onClose={()=>setOpenAppt(null)} onStatus={setStatus} onRemove={(a)=>{ remove(a); setOpenAppt(null); }}/>}
    </div>
  );
}

function AppointmentPopover({ appt, onClose, onStatus, onRemove }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-bg-primary rounded-lg max-w-md w-full p-6 shadow-2xl" data-testid="appt-popover">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-1">APPOINTMENT</div>
            <h3 className="text-lg font-heading font-bold">{appt.patient_name}</h3>
            <p className="text-xs text-fg-muted">
              {wallDateTime(appt.starts_at, CLOCK)} · {appt.duration_min || 45} min
            </p>
          </div>
          <button onClick={onClose} className="text-fg-muted hover:text-fg" data-testid="appt-popover-close"><X className="w-5 h-5"/></button>
        </div>

        <div className="space-y-2 text-sm mb-4">
          <Row label="Service">{appt.service_name || '—'}</Row>
          <Row label="Therapist">{appt.therapist_name || '—'}</Row>
          <Row label="Status"><StatusPill s={appt.status}/></Row>
          {appt.location_name && <Row label="Location">{appt.location_name}</Row>}
          {appt.notes && <Row label="Notes"><span className="text-xs">{appt.notes}</span></Row>}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Link to={`/admin/patients/${appt.patient_id}/chart`} onClick={onClose}
                className="btn-primary text-sm inline-flex items-center justify-center gap-1.5"
                data-testid="appt-open-chart">
            <FileText className="w-3.5 h-3.5"/> Open chart
          </Link>
          <Link to={`/admin/patients/${appt.patient_id}/note?appointment_id=${appt.appointment_id}`} onClick={onClose}
                className="btn-secondary text-sm inline-flex items-center justify-center gap-1.5"
                data-testid="appt-add-note">
            <Edit3 className="w-3.5 h-3.5"/> Add note
          </Link>
        </div>

        <div className="flex justify-end gap-1.5 mt-3 pt-3 border-t border-soft text-xs flex-wrap">
          {appt.status !== 'completed' && (
            <button onClick={()=>{ onStatus(appt, 'completed'); onClose(); }}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[#3A664D] hover:bg-[#E3EFE7]"
                    data-testid="appt-mark-done">
              <Check className="w-3 h-3"/> Mark complete
            </button>
          )}
          {appt.status === 'scheduled' && (
            <button onClick={()=>{ onStatus(appt, 'cancelled'); onClose(); }}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[#B83A3A] hover:bg-[#F3E0DF]"
                    data-testid="appt-cancel">
              <X className="w-3 h-3"/> Cancel
            </button>
          )}
          <button onClick={()=>onRemove(appt)}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[#B83A3A] hover:bg-[#F3E0DF]"
                  data-testid="appt-delete">
            <Trash2 className="w-3 h-3"/> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-xs uppercase tracking-wider text-fg-muted font-medium w-20 shrink-0">{label}</span>
      <span className="flex-1 text-fg">{children}</span>
    </div>
  );
}

function F({ label, children, full }) {
  return (
    <label className={full ? 'md:col-span-3 block' : 'block'}>
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}

function MonthView({ cursor, apptsOnDay, onDayClick, onBook, onApptClick }) {
  const first = startOfMonth(cursor);
  const last = endOfMonth(cursor);
  const gridStart = startOfWeek(first);
  const days = [];
  let d = new Date(gridStart);
  while (d <= last || days.length < 42) {
    days.push(new Date(d));
    d = addDays(d, 1);
    if (days.length >= 42) break;
  }
  const today = new Date();
  return (
    <div className="admin-card">
      <div className="grid grid-cols-7 gap-px bg-[var(--border)] rounded overflow-hidden">
        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
          <div key={d} className="surface p-2 text-xs text-fg-muted text-center font-medium">{d}</div>
        ))}
        {days.map((day, i) => {
          const inMonth = day.getMonth() === cursor.getMonth();
          const isToday = sameDay(day, today);
          const a = apptsOnDay(day);
          return (
            <div key={i} className="surface min-h-[110px] p-2 relative" data-testid={`day-${formatDate(day)}`}>
              <div className="flex items-center justify-between">
                <button onClick={()=>onDayClick(day)} className={`text-xs ${inMonth?'text-fg':'text-fg-muted'} ${isToday?'font-bold text-brand':''}`}>
                  {day.getDate()}
                </button>
                <button onClick={()=>onBook(day)} className="text-[10px] text-brand print:hidden hidden md:inline">+</button>
              </div>
              <div className="space-y-1 mt-1">
                {a.slice(0,3).map(x => <AppPill key={x.appointment_id} a={x} onClick={onApptClick} />)}
                {a.length > 3 && <div className="text-[10px] text-fg-muted">+{a.length-3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function YearView({ cursor, appts, onMonthClick }) {
  const year = cursor.getFullYear();
  const months = Array.from({length:12}, (_,m) => new Date(year, m, 1));
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {months.map(m => {
        const count = appts.filter(a => wallDay(a.starts_at).slice(0,7) === `${year}-${String(m.getMonth()+1).padStart(2,'0')}`).length;
        return (
          <button key={m.getMonth()} onClick={()=>onMonthClick(m)} className="admin-card text-left hover:border-[#003D80]" data-testid={`month-${m.getMonth()}`}>
            <div className="font-heading font-semibold">{m.toLocaleDateString(undefined,{month:'long'})}</div>
            <div className="text-xs text-fg-muted mt-1">{count} appointments</div>
            <div className="mt-3 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-brand"/>
              <div className="h-1 bg-[#F2EBDB] flex-1 rounded"><div className="h-1 bg-brand rounded" style={{width: `${Math.min(count*10, 100)}%`}}/></div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function WeekView({ cursor, apptsOnDay, onDayClick, onApptClick }) {
  const start = startOfWeek(cursor);
  const days = Array.from({length:7}, (_,i) => addDays(start, i));
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-px bg-[var(--border)] rounded overflow-hidden">
      {days.map((d,i) => {
        const a = apptsOnDay(d);
        return (
          <div key={i} className="surface p-3 min-h-[180px]" data-testid={`week-${formatDate(d)}`}>
            <button onClick={()=>onDayClick(d)} className="text-xs text-fg-muted">
              {d.toLocaleDateString(undefined,{weekday:'short'})} {d.getDate()}
            </button>
            <div className="space-y-1 mt-2">
              {a.map(x => <AppPill key={x.appointment_id} a={x} onClick={onApptClick} />)}
              {!a.length && <div className="text-xs text-fg-muted">—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayView({ cursor, appts, onBook, onStatus, onRemove, onReschedule, onApptClick }) {
  const hours = Array.from({length:14}, (_,i) => i+7); // 07..20
  const [overHour, setOverHour] = useState(null);
  const [dragId, setDragId] = useState(null);

  const onDragStart = (e, a) => {
    e.dataTransfer.setData('text/plain', a.appointment_id);
    e.dataTransfer.effectAllowed = 'move';
    setDragId(a.appointment_id);
  };
  const onDragEnd = () => { setDragId(null); setOverHour(null); };
  const onDragOver = (e, h) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverHour(h);
  };
  const onDrop = (e, h) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    const a = appts.find(x => x.appointment_id === id);
    setOverHour(null); setDragId(null);
    if (!a) return;
    const existing = new Date(a.starts_at);
    const newDate = new Date(cursor);
    newDate.setHours(h, existing.getMinutes(), 0, 0);
    if (newDate.toISOString() === existing.toISOString()) return;
    onReschedule(a, newDate);
  };

  return (
    <div className="admin-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium">{cursor.toLocaleDateString(undefined,{weekday:'long', month:'long', day:'numeric', year:'numeric'})}</div>
        <div className="flex items-center gap-2 print:hidden">
          <span className="text-[11px] text-fg-muted hidden md:inline">Drag any appointment to a different hour to reschedule</span>
          <button onClick={()=>onBook(cursor)} className="btn-secondary text-xs inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5"/> Book this day</button>
        </div>
      </div>
      <div className="divide-y border-soft">
        {hours.map(h => {
          const slot = appts.filter(a => wallHour(a.starts_at) === h);
          const isOver = overHour === h;
          return (
            <div key={h}
                 onDragOver={(e)=>onDragOver(e, h)}
                 onDragLeave={()=>setOverHour(null)}
                 onDrop={(e)=>onDrop(e, h)}
                 className={`flex gap-3 py-2 transition rounded ${isOver ? 'bg-[var(--accent-soft)] ring-2 ring-[var(--accent)]/40' : ''}`}
                 data-testid={`hour-slot-${h}`}>
              <div className="w-14 text-xs text-fg-muted font-mono">{String(h).padStart(2,'0')}:00</div>
              <div className="flex-1 space-y-1">
                {slot.map(a => {
                  const tint = tintFor(a.discipline);
                  const cardStyle = tint && a.status !== 'completed' && a.status !== 'cancelled'
                    ? { borderLeft: `4px solid ${tint.dot}`, background: tint.bg + '40' }
                    : {};
                  return (
                    <div key={a.appointment_id}
                         draggable
                         onDragStart={(e)=>onDragStart(e, a)}
                         onDragEnd={onDragEnd}
                         className={`surface border border-soft rounded-lg p-2 flex items-center justify-between cursor-grab active:cursor-grabbing transition ${dragId === a.appointment_id ? 'opacity-40' : 'hover:shadow-sm'}`}
                         style={cardStyle}
                         data-testid={`day-appt-${a.appointment_id}`}>
                      <div className="flex-1 cursor-pointer" onClick={()=>onApptClick?.(a)} data-testid={`day-appt-info-${a.appointment_id}`}>
                        <div className="text-sm font-medium hover:text-brand transition">{a.patient_name} · <span className="text-fg-muted">{formatTime(a.starts_at)}</span></div>
                        <div className="text-xs text-fg-muted">
                          {a.service_name || 'Session'} · {a.duration_min} min · {a.price} {a.currency}
                          {a.discipline && <span className="ml-1.5 capitalize text-[10px] inline-flex items-center gap-0.5" style={{ color: tint?.dot || 'inherit' }}>· {a.discipline.replace('_', ' ')}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 print:hidden">
                        <StatusPill s={a.status} />
                        {a.prepay_status === 'paid' && <span className="badge-success">Prepay</span>}
                        <button onClick={()=>onStatus(a,'completed')} className="text-[#3A664D] p-1" title="Complete"><Check className="w-4 h-4"/></button>
                        <button onClick={()=>onStatus(a,'cancelled')} className="text-[#B83A3A] p-1" title="Cancel"><X className="w-4 h-4"/></button>
                        <button onClick={()=>onRemove(a)} className="text-fg-muted hover:text-[#B83A3A] p-1"><Trash2 className="w-4 h-4"/></button>
                      </div>
                    </div>
                  );
                })}
                {!slot.length && <div className="text-[11px] text-fg-muted">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Iter 68 — color-code calendar entries by discipline (chiro/physio/osteo/massage/fitness)
// in addition to status. Each discipline has its own hue so the clinician can
// scan the week and see treatment mix at a glance.
const DISCIPLINE_TINT = {
  physiotherapy:    { bg: '#E0EAFB', fg: '#003D80', dot: '#003D80' },
  osteopathy:       { bg: '#F0E6F8', fg: '#5A3175', dot: '#7B4FA5' },
  chiropractic:     { bg: '#FFE7D9', fg: '#9A4A1A', dot: '#D86E2C' },
  massage_therapy:  { bg: '#E8F5E8', fg: '#3A664D', dot: '#5A8A6A' },
  massage:          { bg: '#E8F5E8', fg: '#3A664D', dot: '#5A8A6A' },
  fitness:          { bg: '#FFF4D6', fg: '#8A5A00', dot: '#FFB000' },
};

function tintFor(discipline) {
  if (!discipline) return null;
  return DISCIPLINE_TINT[discipline] || DISCIPLINE_TINT[discipline.toLowerCase().replace(/\s+/g, '_')] || null;
}

function AppPill({ a, onClick }) {
  // Iter 67/68 — clicking the pill opens the appointment popover.
  // Color cascade: status > discipline tint > prepay > default.
  const tint = tintFor(a.discipline);
  let bg = 'white', fg = '#0F1B2D', border = '1px solid var(--border)';
  let extraCls = '';

  if (a.status === 'completed') {
    bg = '#E3EFE7'; fg = '#3A664D';
  } else if (a.status === 'cancelled') {
    bg = '#F3E0DF'; fg = '#B83A3A';
    extraCls = 'line-through';
  } else if (tint) {
    bg = tint.bg; fg = tint.fg;
  } else if (a.prepay_status === 'paid') {
    bg = '#F2EBDB'; fg = 'var(--brand)';
  }

  return (
    <button onClick={(e) => { e.stopPropagation(); onClick?.(a); }}
            type="button"
            className={`text-[11px] rounded px-1.5 py-0.5 truncate w-full text-left hover:ring-2 hover:ring-brand/50 transition cursor-pointer ${extraCls}`}
            style={{ background: bg, color: fg, border }}
            title={`${a.patient_name} ${formatTime(a.starts_at)}${a.discipline ? ` · ${a.discipline}` : ''} — click to open`}
            data-testid={`appt-pill-${a.appointment_id}`}>
      {tint && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: tint.dot }}/>}
      {formatTime(a.starts_at)} {a.patient_name}
    </button>
  );
}

function StatusPill({ s }) {
  const cls = s === 'completed' ? 'badge-success' : s === 'scheduled' ? 'badge-neutral'
            : s === 'cancelled' ? 'badge-error' : 'badge-warn';
  return <span className={cls}>{s}</span>;
}
